import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import SearchFilters from './components/SearchFilters';
import AppDetailModal from './components/AppDetailModal';
import VisibilityModal from './components/VisibilityModal';
import AppFormModal from './components/AppFormModal';
import ConfirmModal from './components/ConfirmModal';
import { mockApplications, mockCategories } from './mockData';
import type { Application } from './types';

export default function AppsPage() {
  const [applications, setApplications] = useState(mockApplications);
  const [filteredApps, setFilteredApps] = useState(mockApplications);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [detailApp, setDetailApp] = useState<Application | null>(null);
  const [visibilityApp, setVisibilityApp] = useState<Application | null>(null);
  const [formApp, setFormApp] = useState<any>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const handleSearch = (keyword: string) => {
    let filtered = applications;
    if (keyword) {
      filtered = filtered.filter((app) =>
        app.name.toLowerCase().includes(keyword.toLowerCase())
      );
    }
    setFilteredApps(filtered);
  };

  const handleCategoryChange = (categoryId: string) => {
    let filtered = applications;
    if (categoryId) {
      filtered = filtered.filter((app) => app.categoryId === categoryId);
    }
    setFilteredApps(filtered);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedApps(filteredApps.map((app) => app.id));
    } else {
      setSelectedApps([]);
    }
  };

  const handleSelectApp = (appId: string) => {
    setSelectedApps((prev) =>
      prev.includes(appId) ? prev.filter((id) => id !== appId) : [...prev, appId]
    );
  };

  const handleBatchEnable = () => {
    setConfirmModal({
      isOpen: true,
      title: '批量启用',
      message: `确定要启用选中的 ${selectedApps.length} 个应用吗？`,
      type: 'info',
      onConfirm: () => {
        setApplications((prev) =>
          prev.map((app) =>
            selectedApps.includes(app.id) ? { ...app, status: true } : app
          )
        );
        setFilteredApps((prev) =>
          prev.map((app) =>
            selectedApps.includes(app.id) ? { ...app, status: true } : app
          )
        );
        setSelectedApps([]);
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const handleBatchDisable = () => {
    setConfirmModal({
      isOpen: true,
      title: '批量禁用',
      message: `确定要禁用选中的 ${selectedApps.length} 个应用吗？`,
      type: 'warning',
      onConfirm: () => {
        setApplications((prev) =>
          prev.map((app) =>
            selectedApps.includes(app.id) ? { ...app, status: false } : app
          )
        );
        setFilteredApps((prev) =>
          prev.map((app) =>
            selectedApps.includes(app.id) ? { ...app, status: false } : app
          )
        );
        setSelectedApps([]);
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const handleBatchDelete = () => {
    setConfirmModal({
      isOpen: true,
      title: '批量删除',
      message: `确定要删除选中的 ${selectedApps.length} 个应用吗？此操作不可恢复。`,
      type: 'danger',
      onConfirm: () => {
        setApplications((prev) => prev.filter((app) => !selectedApps.includes(app.id)));
        setFilteredApps((prev) => prev.filter((app) => !selectedApps.includes(app.id)));
        setSelectedApps([]);
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const handleToggleStatus = (appId: string) => {
    setApplications((prev) =>
      prev.map((app) =>
        app.id === appId ? { ...app, status: !app.status } : app
      )
    );
    setFilteredApps((prev) =>
      prev.map((app) =>
        app.id === appId ? { ...app, status: !app.status } : app
      )
    );
  };

  const handleDelete = (appId: string) => {
    setConfirmModal({
      isOpen: true,
      title: '删除应用',
      message: '确定要删除此应用吗？此操作不可恢复。',
      type: 'danger',
      onConfirm: () => {
        setApplications((prev) => prev.filter((app) => app.id !== appId));
        setFilteredApps((prev) => prev.filter((app) => app.id !== appId));
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const handleSaveVisibility = (
    visibility: 'all' | 'none' | 'partial',
    tenantIds?: string[]
  ) => {
    if (!visibilityApp) return;

    const userPermission = visibility === 'all' ? 'all' : visibility === 'none' ? 'none' : 'partial';
    
    setApplications((prev) =>
      prev.map((app) =>
        app.id === visibilityApp.id
          ? { ...app, userPermission, visibleTenants: tenantIds || [] }
          : app
      )
    );
    setFilteredApps((prev) =>
      prev.map((app) =>
        app.id === visibilityApp.id
          ? { ...app, userPermission, visibleTenants: tenantIds || [] }
          : app
      )
    );
    setVisibilityApp(null);
  };

  const handleSaveForm = (data: any) => {
    if (formApp) {
      setApplications((prev) =>
        prev.map((app) =>
          app.id === formApp.id ? { ...app, ...data } : app
        )
      );
      setFilteredApps((prev) =>
        prev.map((app) =>
          app.id === formApp.id ? { ...app, ...data } : app
        )
      );
    } else {
      const newApp: Application = {
        id: `APP${String(applications.length + 1).padStart(3, '0')}`,
        source: 'platform',
        icon: 'https://public.readdy.ai/ai/img_res/371c3e41b10a69f9128c300e632bf5b3.jpg',
        downloads: 0,
        status: true,
        userPermission: 'all',
        price: 0,
        category: mockCategories.find(c => c.children?.some(sc => sc.id === data.categoryId))?.name + ' / ' + 
                  mockCategories.flatMap(c => c.children || []).find(sc => sc.id === data.categoryId)?.name || '',
        visibleTenants: [],
        ...data
      };
      setApplications((prev) => [...prev, newApp]);
      setFilteredApps((prev) => [...prev, newApp]);
    }
    setShowFormModal(false);
    setFormApp(null);
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">应用管理</h1>
            <p className="text-sm text-gray-500 mt-1">
              管理云电脑的应用市场，包括平台上传以及用户上传的镜像
            </p>
          </div>
          <button
            onClick={() => {
              setFormApp(null);
              setShowFormModal(true);
            }}
            className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap"
          >
            <i className="ri-add-line mr-2"></i>
            新建应用
          </button>
        </div>

        <SearchFilters
          onSearch={handleSearch}
          onCategoryChange={handleCategoryChange}
          categories={mockCategories}
        />

        {selectedApps.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="text-sm text-blue-800">
              已选择 <span className="font-semibold">{selectedApps.length}</span> 个应用
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleBatchEnable}
                className="px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <i className="ri-check-line mr-2"></i>
                批量启用
              </button>
              <button
                onClick={handleBatchDisable}
                className="px-4 py-2 bg-white text-orange-700 border border-orange-300 rounded-lg hover:bg-orange-50 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <i className="ri-close-line mr-2"></i>
                批量禁用
              </button>
              <button
                onClick={handleBatchDelete}
                className="px-4 py-2 bg-white text-red-700 border border-red-300 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-2"></i>
                批量删除
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedApps.length === filteredApps.length && filteredApps.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 text-teal-600 focus:ring-teal-500 rounded"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    应用来源
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    应用信息
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    版本号
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    下载量
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    状态
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    开发者
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    应用分类
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    用户权限
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    价格
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredApps.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedApps.includes(app.id)}
                        onChange={() => handleSelectApp(app.id)}
                        className="w-4 h-4 text-teal-600 focus:ring-teal-500 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        app.source === 'platform'
                          ? 'bg-teal-100 text-teal-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {app.source === 'platform' ? '平台上传' : '用户上传'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={app.icon}
                          alt={app.name}
                          className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{app.name}</div>
                          <div className="text-xs text-gray-500">{app.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {app.version}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {app.downloads.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus(app.id)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          app.status ? 'bg-teal-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            app.status ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {app.developer}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {app.category}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        app.userPermission === 'all'
                          ? 'bg-green-100 text-green-700'
                          : app.userPermission === 'partial'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {app.userPermission === 'all'
                          ? '全部可见'
                          : app.userPermission === 'partial'
                          ? '部分可见'
                          : '不可见'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {app.price === 0 ? '免费' : `¥${app.price}`}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setDetailApp(app)}
                          className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="查看详情"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                        {app.source === 'platform' && (
                          <button
                            onClick={() => setVisibilityApp(app)}
                            className="w-8 h-8 flex items-center justify-center text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="设置可见性"
                          >
                            <i className="ri-settings-3-line"></i>
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setFormApp(app);
                            setShowFormModal(true);
                          }}
                          className="w-8 h-8 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="编辑"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(app.id)}
                          className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="删除"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredApps.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">暂无应用数据</p>
            </div>
          )}
        </div>
      </div>

      <AppDetailModal app={detailApp} onClose={() => setDetailApp(null)} />
      <VisibilityModal
        app={visibilityApp}
        onClose={() => setVisibilityApp(null)}
        onSave={handleSaveVisibility}
      />
      {showFormModal && (
        <AppFormModal
          app={formApp}
          categories={mockCategories}
          onClose={() => {
            setShowFormModal(false);
            setFormApp(null);
          }}
          onSave={handleSaveForm}
        />
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
      />
    </MainLayout>
  );
}
